import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import Loader from '../../components/Loader';
import { toast } from 'react-toastify';
import ApiService from '../../services/ApiService';
import AuthService from '../../services/AuthService';
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { formatCurrency, formatDate } from '../../utils/helper';
import { Dropdown } from 'primereact/dropdown';
import PaymentForm from '../../components/PaymentForm';
import TimeSlotSelect from '../../components/TimeSlotSelect';
import BookingFlow from '../../components/BookingFlow';

const AddUpdate = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [isLoader, setIsLoader] = useState(false);
    const [customers, setCustomers] = useState([]);
    const [staff, setStaff] = useState([]);
    const [branches, setBranches] = useState([]);
    const [services, setServices] = useState([]);
    const [selectedServices, setSelectedServices] = useState([]);
    const [showPayment, setShowPayment] = useState(false);
    const [selectedServicePrice, setSelectedServicePrice] = useState(0);
    const [paymentInfo, setPaymentInfo] = useState(null);
    const [transactions, setTransactions] = useState([]);
    
    const [formData, setFormData] = useState({
        customer_id: '',
        branch_id: '',
        staff_id: '',
        date: new Date(),
        time: '09:00',
        status: 'pending',
        notes: '',
    });

    const authUser = AuthService.getCurrentUser();
    const isCustomer = authUser?.role === 'customer';

    useEffect(() => {
        getBranches();
        getServices();
        if (!isCustomer) {
            getCustomers();
            getStaff();
        }
        if (id) {
            getSingleData();
        }
    }, [id, isCustomer]);

    const getCustomers = async () => {
        try {
            const response = await ApiService.request({
                method: 'GET',
                url: '/admin/customers', // Only admin can list all customers
                data: { per_page: 1000 }
            });
            if (response.data.status) {
                const formatted = (response.data.data.customers || []).map(p => ({
                    ...p,
                    label: `${p.name} (${p.phone})`
                }));
                setCustomers(formatted);
            }
        } catch (error) {
            console.error(error);
        }
    };

    const getStaff = async () => {
        try {
            const response = await ApiService.request({
                method: 'GET',
                url: '/admin/users',
                data: { role: 'staff', per_page: 1000 }
            });
            if (response.data.status) {
                setStaff(response.data.data.users || []);
            }
        } catch (error) {
            console.error(error);
        }
    };

    const getBranches = async () => {
        try {
            const isCustomer = authUser?.role === 'customer';
            const url = isCustomer ? '/customer/branches' : '/admin/branches';
            const response = await ApiService.request({
                method: 'GET',
                url: url,
                data: { per_page: 1000 }
            });
            if (response.data.status) {
                setBranches(response.data.data.branches || []);
            }
        } catch (error) {
            console.error(error);
        }
    };

    const getServices = async () => {
        try {
            const isCustomer = authUser?.role === 'customer';
            const url = isCustomer ? '/customer/services' : '/admin/services';
            const response = await ApiService.request({
                method: 'GET',
                url: url,
                data: { per_page: 1000 }
            });
            if (response.data.status) {
                setServices(response.data.data.services || []);
            }
        } catch (error) {
            console.error(error);
        }
    };

    const getSingleData = async () => {
        try {
            setIsLoader(true);
            const response = await ApiService.request({
                method: 'GET',
                url: isCustomer ? `/customer/appointments/${id}` : `/admin/appointments/${id}`,
            });
            setIsLoader(false);
                if (response.data.status) {
                    const data = response.data.data;
                    setFormData({
                        customer_id: data.customer_id,
                        branch_id: data.branch_id,
                        staff_id: data.staff_id,
                        date: new Date(data.date),
                        time: data.time ? data.time.split(' ')[0] : '',
                        status: data.status,
                        notes: data.notes || '',
                });
                
                // Set payment information for display
                setPaymentInfo({
                    total_amount: data.total_amount,
                    paid_amount: data.paid_amount,
                    payment_status: data.payment_status,
                    transaction_id: data.transaction_id,
                    payment_method: data.payment_method,
                    deposit_amount: data.deposit_amount,
                });

                if (data.transactions) {
                    setTransactions(data.transactions);
                }

                if (data.services && data.services.length > 0) {
                    const ids = data.services.map(s => s.id);
                    setSelectedServices(ids);
                    // Calculate price based on current service prices or use stored price
                    // For now using stored price
                    setSelectedServicePrice(data.price); 
               }
            } else {
                toast.error(response.data.message);
                navigate('/appointments');
            }
        } catch (error) {
            setIsLoader(false);
            console.error(error);
            toast.error('Error fetching appointment details');
            navigate('/appointments');
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleServiceSelect = (serviceId) => {
        let newSelected;
        if (selectedServices.includes(serviceId)) {
            newSelected = selectedServices.filter(id => id !== serviceId);
        } else {
            newSelected = [...selectedServices, serviceId];
        }
        setSelectedServices(newSelected);
        
        // Update total price
        const total = services
            .filter(s => newSelected.includes(s.id))
            .reduce((sum, s) => sum + parseFloat(s.price), 0);
        setSelectedServicePrice(total);
    };

    const handlePaymentSuccess = async (paymentIntent) => {
        // Submit the form after payment
        // We can add payment info to payload if needed
        await submitForm(paymentIntent.id);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        // If customer is creating appointment, show payment first
        if (isCustomer && !id && !showPayment) {
            setShowPayment(true);
            return;
        }

        await submitForm();
    };

    const submitForm = async (paymentIntentId = null) => {
        setIsLoader(true);

        const date = new Date(formData.date);
        date.setMinutes(date.getMinutes() - date.getTimezoneOffset());
        const dateString = date.toISOString().split('T')[0];

        const payload = {
            ...formData,
            service_ids: selectedServices,
            date: dateString, // Format YYYY-MM-DD with local time fix
            customer_id: isCustomer ? authUser.id : formData.customer_id, // Use auth ID if customer
            payment_intent_id: paymentIntentId
        };

        try {
            const url = isCustomer 
                ? (id ? `/customer/appointments/${id}` : '/customer/appointments') 
                : (id ? `/admin/appointments/${id}` : '/admin/appointments');

            const response = await ApiService.request({
                method: id ? 'PUT' : 'POST',
                url: url,
                data: payload
            });
            setIsLoader(false);
            if (response.data.status) {
                toast.success(response.data.message);
                navigate('/appointments');
            } else {
                toast.error(response.data.message);
            }
        } catch (error) {
            setIsLoader(false);
            console.error(error);
            toast.error('Error saving appointment');
        }
    };

    if (isCustomer && !id) {
        return <BookingFlow mode="customer" />;
    }

    return (
        <div className="d-flex flex-column flex-column-fluid">
            <div id="kt_app_toolbar" className="app-toolbar py-3 py-lg-6">
                <div id="kt_app_toolbar_container" className="app-container container-fluid d-flex flex-stack">
                    <div className="page-title d-flex flex-column justify-content-center flex-wrap me-3">
                        <h1 className="page-heading d-flex text-dark fw-bold fs-3 flex-column justify-content-center my-0">
                            {id ? 'Update Appointment' : 'New Appointment'}
                        </h1>
                    </div>
                    <div className="d-flex align-items-center gap-2 gap-lg-3">
                        <Link to="/appointments" className="btn btn-sm fw-bold btn-light">
                            Back to List
                        </Link>
                    </div>
                </div>
            </div>

            <div id="kt_app_content" className="app-content flex-column-fluid">
                <div id="kt_app_content_container" className="app-container container-fluid">
                    <div className="card">
                        <div className="card-body p-9">
                            <form className="form" onSubmit={handleSubmit}>
                                
                                {!isCustomer && (
                                    <div className="mb-10">
                                        <h3 className="fw-bold text-dark mb-5">1. Customer Details</h3>
                                        <div className="form-group">
                                            <label className="form-label required">Select Customer</label>
                                            <Dropdown
                                                value={formData.customer_id}
                                                options={customers}
                                                onChange={(e) => setFormData({ ...formData, customer_id: e.value })}
                                                optionLabel="label"
                                                optionValue="id"
                                                filter
                                                placeholder="Select Customer"
                                                className="w-100"
                                            />
                                        </div>
                                    </div>
                                )}

                                <div className="mb-10">
                                    <h3 className="fw-bold text-dark mb-5">{!isCustomer ? '2.' : '1.'} Select Branch</h3>
                                    <div className="row g-4">
                                        {branches.map(branch => (
                                            <div className="col-md-6 col-lg-4" key={branch.id}>
                                                <div 
                                                    className={`card h-100 cursor-pointer transition-all ${formData.branch_id == branch.id ? 'border border-base bg-light-base shadow-sm' : 'border-dashed hover-elevate-up'}`}
                                                    onClick={() => setFormData({ ...formData, branch_id: branch.id })}
                                                    style={{ borderWidth: '2px' }}
                                                >
                                                    <div className="card-body d-flex align-items-center p-5">
                                                            <div className={`symbol symbol-50px me-4 ${formData.branch_id == branch.id ? 'bg-base' : 'bg-light'}`}>
                                                            <span className="symbol-label">
                                                                <i className={`fas fa-store fs-2 ${formData.branch_id == branch.id ? 'text-base' : 'text-dark'}`}></i>
                                                            </span>
                                                            </div>
                                                            <div className="flex-grow-1">
                                                                <div className="fw-bold text-gray-800 fs-5 mb-1">{branch.name}</div>
                                                                <div className="text-muted fs-7">{branch.address}</div>
                                                            </div>
                                                            {formData.branch_id == branch.id && <i className="fas fa-check-circle text-base fs-2 ms-3"></i>}
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                <div className="mb-10">
                                    <div className="d-flex justify-content-between align-items-center mb-5">
                                        <h3 className="fw-bold text-dark mb-0">{!isCustomer ? '3.' : '2.'} Select Services</h3>
                                        <div className="badge badge-light-primary fs-6 px-4 py-2">
                                            Total: {formatCurrency(selectedServicePrice)}
                                        </div>
                                    </div>
                                    <div className="row g-4">
                                        {services.map(service => (
                                            <div className="col-md-6 col-lg-4" key={service.id}>
                                                <div 
                                                    className={`card h-100 cursor-pointer transition-all ${selectedServices.includes(service.id) ? 'border border-base bg-light-base shadow-sm' : 'border-dashed hover-elevate-up'}`}
                                                    onClick={() => handleServiceSelect(service.id)}
                                                    style={{ borderWidth: '2px' }}
                                                >
                                                    <div className="card-body p-5">
                                                        <div className="d-flex justify-content-between align-items-center mb-3">
                                                            <div className="badge badge-light-primary">{service.duration_minutes} mins</div>
                                                            <div className="fw-bolder text-base fs-4">{formatCurrency(service.price)}</div>
                                                        </div>
                                                        <div className="fw-bold fs-5 text-dark mb-1">{service.name}</div>
                                                        
                                                        {selectedServices.includes(service.id) && (
                                                            <div className="position-absolute top-0 end-0 m-1">
                                                                <i className="fas fa-check-circle text-base fs-3"></i>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                <div className="mb-10">
                                    <h3 className="fw-bold text-dark mb-5">{!isCustomer ? '4.' : '3.'} Schedule & Details</h3>
                                    <div className="row mb-5">
                                        <div className="col-md-6">
                                            <div className="form-group">
                                                <label className="form-label required">Date</label>
                                                <DatePicker 
                                                    selected={formData.date} 
                                                    onChange={(date) => setFormData({ ...formData, date })} 
                                                    className="form-control form-control-solid"
                                                    dateFormat="yyyy-MM-dd"
                                                    minDate={new Date()}
                                                    wrapperClassName="w-100"
                                                />
                                            </div>
                                        </div>
                                        <div className="col-md-6">
                                            <div className="form-group">
                                                <label className="form-label required">Time</label>
                                                <TimeSlotSelect
                                                    value={formData.time}
                                                    onChange={(val) => setFormData(prev => ({ ...prev, time: val }))}
                                                    branchId={formData.branch_id}
                                                    date={formData.date}
                                                    apiPrefix={isCustomer ? '/customer' : '/admin'}
                                                    size="default"
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    {!isCustomer && (
                                        <div className="row mb-5">
                                            <div className="col-md-6">
                                                <div className="form-group">
                                                    <label className="form-label">Staff (Optional)</label>
                                                    <select 
                                                        className="form-select form-select-solid"
                                                        name="staff_id"
                                                        value={formData.staff_id}
                                                        onChange={handleChange}
                                                    >
                                                        <option value="">Select Staff</option>
                                                        {staff.map(user => (
                                                            <option key={user.id} value={user.id}>{user.name}</option>
                                                        ))}
                                                    </select>
                                                </div>
                                            </div>
                                            <div className="col-md-6">
                                                <div className="form-group">
                                                    <label className="form-label">Status</label>
                                                    <select 
                                                        className="form-select form-select-solid"
                                                        name="status"
                                                        value={formData.status}
                                                        onChange={handleChange}
                                                    >
                                                        <option value="pending">Pending</option>
                                                        <option value="confirmed">Confirmed</option>
                                                        <option value="completed">Completed</option>
                                                        <option value="cancelled">Cancelled</option>
                                                    </select>
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    <div className="form-group">
                                        <label className="form-label">Notes</label>
                                        <textarea 
                                            className="form-control form-control-solid"
                                            name="notes"
                                            value={formData.notes}
                                            onChange={handleChange}
                                            rows="3"
                                            placeholder="Any special instructions..."
                                        ></textarea>
                                    </div>
                                </div>

                                {paymentInfo && (
                                    <div className="mb-10">
                                        <h3 className="fw-bold text-dark mb-5">Payment Information</h3>
                                        <div className="row g-4">
                                            <div className="col-md-6 col-lg-4">
                                                <div className="card bg-light border-dashed p-4 h-100">
                                                    <div className="fs-6 text-gray-600 mb-1">Total Amount</div>
                                                    <div className="fs-2 fw-bold text-dark">{formatCurrency(paymentInfo.total_amount || 0)}</div>
                                                </div>
                                            </div>
                                            <div className="col-md-6 col-lg-4">
                                                <div className="card bg-light border-dashed p-4 h-100">
                                                    <div className="fs-6 text-gray-600 mb-1">Paid Amount</div>
                                                    <div className="fs-2 fw-bold text-success">{formatCurrency(paymentInfo.paid_amount || 0)}</div>
                                                </div>
                                            </div>
                                            <div className="col-md-6 col-lg-4">
                                                <div className="card bg-light border-dashed p-4 h-100">
                                                    <div className="fs-6 text-gray-600 mb-1">Remaining Due</div>
                                                    <div className="fs-2 fw-bold text-danger">
                                                        {formatCurrency((parseFloat(paymentInfo.total_amount) || 0) - (parseFloat(paymentInfo.paid_amount) || 0))}
                                                    </div>
                                                </div>
                                            </div>
                                            
                                            <div className="col-md-6">
                                                <div className="form-group">
                                                    <label className="form-label fw-bold">Payment Status</label>
                                                    <div className={`form-control form-control-solid fw-bolder text-uppercase ${
                                                        paymentInfo.payment_status === 'paid' ? 'text-success' : 
                                                        paymentInfo.payment_status === 'partial' ? 'text-warning' : 'text-danger'
                                                    }`}>
                                                        {paymentInfo.payment_status || 'Pending'}
                                                    </div>
                                                </div>
                                            </div>
                                            
                                            <div className="col-md-6">
                                                <div className="form-group">
                                                    <label className="form-label fw-bold">Transaction ID</label>
                                                    <input 
                                                        type="text" 
                                                        className="form-control form-control-solid" 
                                                        value={paymentInfo.transaction_id || 'N/A'} 
                                                        readOnly 
                                                    />
                                                </div>
                                            </div>
                                            
                                            {paymentInfo.payment_method && (
                                                <div className="col-md-6">
                                                    <div className="form-group">
                                                        <label className="form-label fw-bold">Payment Method</label>
                                                        <input 
                                                            type="text" 
                                                            className="form-control form-control-solid text-uppercase" 
                                                            value={paymentInfo.payment_method} 
                                                            readOnly 
                                                        />
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}

                                {transactions.length > 0 && (
                                    <div className="mb-10">
                                        <h3 className="fw-bold text-dark mb-5">Transaction History</h3>
                                        <div className="table-responsive">
                                            <table className="table align-middle table-row-dashed fs-6 gy-5">
                                                <thead>
                                                    <tr className="text-start text-muted fw-bold fs-7 text-uppercase gs-0">
                                                        <th>Date</th>
                                                        <th>Type</th>
                                                        <th>Amount</th>
                                                        <th>Status</th>
                                                        <th>Transaction ID</th>
                                                    </tr>
                                                </thead>
                                                <tbody className="text-gray-600 fw-semibold">
                                                    {transactions.map((tx) => (
                                                        <tr key={tx.id}>
                                                            <td>{formatDate(tx.created_at, 'MMM D, YYYY HH:mm')}</td>
                                                            <td className="text-capitalize">{tx.type.replace('_', ' ')}</td>
                                                            <td>{formatCurrency(tx.amount)}</td>
                                                            <td>
                                                                <span className={`badge badge-light-${tx.status === 'success' ? 'success' : 'danger'}`}>
                                                                    {tx.status}
                                                                </span>
                                                            </td>
                                                            <td>{tx.stripe_transaction_id || '-'}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                )}

                                <div className="separator separator-dashed my-10"></div>

                                <div className="d-flex align-items-center justify-content-end gap-3">
                                    <Link to="/appointments" className="btn btn-light">Cancel</Link>
                                    <button type="submit" className="btn btn-base" disabled={isLoader}>
                                        {isLoader ? <Loader /> : (isCustomer && !id ? `Pay ${formatCurrency(selectedServicePrice)} & Book` : 'Save Appointment')}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
            {showPayment && (
                <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Payment</h5>
                                <button type="button" className="btn-close" onClick={() => setShowPayment(false)}></button>
                            </div>
                            <div className="modal-body">
                                <PaymentForm 
                                    amount={selectedServicePrice} 
                                    onSuccess={handlePaymentSuccess} 
                                />
                            </div>
                            <div className="modal-footer">
                                <button 
                                    type="button" 
                                    className="btn btn-light" 
                                    onClick={() => setShowPayment(false)}
                                >
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AddUpdate;
